/**
 * How history rows are sectioned, which depends entirely on which band is being looked at.
 *
 * The pickup board splits after-school into **runs** — one school × one grade band — because
 * that pair is the unit of dismissal work: TK & K and G1–G5 are collected at different times,
 * by different staff, at different doors. History inherits that split, so an admin reading
 * the archive sees it organised the way the day was actually organised.
 *
 * Chinese and Math do not split that way. Nobody is dismissed from them, the class *is* a
 * grade (`chinese:3`), and every child in the room is from a different school — sectioning by
 * school would produce a dozen headings of one row each, describing nothing. So they section
 * by grade alone.
 *
 * Camps and Enrichment do not section at all. A camp week is one mixed group with no band and
 * no run, and an enrichment class is one room; the flat table is already the truth.
 */

import {
  GRADE_BAND_LABEL,
  UNKNOWN_GRADE,
  UNKNOWN_SCHOOL,
  formatGradeLabel,
  gradeBand,
  gradeRank,
  normalizeGrade,
  shortSchoolLabel,
  type GradeBand,
} from './rosterGrouping';
import { attendanceClassLabel, type AttendanceHistoryRow } from './attendanceHistory';
import type { HistoryBand } from '../hooks/useAttendanceHistory';

export interface AttendanceSegment {
  key: string;
  /** The heading, e.g. "Audubon · TK & K" or "Chinese G3". */
  label: string;
  rows: AttendanceHistoryRow[];
}

/** Which bands section their rows, and by what. */
export function segmentsBy(band: HistoryBand): 'run' | 'grade' | 'none' {
  if (band === 'afterschool') return 'run';
  if (band === 'chinese' || band === 'math') return 'grade';
  // Camps and Enrichment: one mixed group with no band and no run to split on.
  return 'none';
}

function bandOf(row: AttendanceHistoryRow): GradeBand | null {
  const normalized = normalizeGrade(row.studentGrade);
  return normalized === null ? null : gradeBand(normalized);
}

/**
 * Sort weight for a section, so the order is the board's order rather than the order rows
 * happened to arrive in: schools alphabetically, and inside each, TK & K before G1–G5 before
 * the exceptions.
 */
const BAND_WEIGHT: Record<GradeBand, number> = { tk_k: 0, g1_5: 1, other: 2 };

/**
 * Group one page of rows into sections.
 *
 * Sectioning is presentational and applies to the rows in hand, not to the whole result set —
 * a section on screen means "these rows, on this page", and the page count above the table is
 * the number that speaks for the query. Grouping server-side would mean ordering by a jsonb
 * field on a joined table, which PostgREST cannot express and Postgres could not index.
 */
export function buildAttendanceSegments(
  rows: readonly AttendanceHistoryRow[],
  band: HistoryBand,
): AttendanceSegment[] {
  const mode = segmentsBy(band);
  if (mode === 'none' || rows.length === 0) return [];

  const sections = new Map<string, AttendanceSegment & { sort: [string, number] }>();

  for (const row of rows) {
    let key: string;
    let label: string;
    let sort: [string, number];

    if (mode === 'run') {
      const school = row.studentSchool?.trim()
        ? shortSchoolLabel(row.studentSchool.trim())
        : UNKNOWN_SCHOOL;
      const rowBand = bandOf(row);
      // A grade nobody can read is its own section rather than being filed under G1–G5. On a
      // pickup board that child is a question to answer, not one to hide among the others.
      const bandLabel = rowBand ? GRADE_BAND_LABEL[rowBand] : UNKNOWN_GRADE;
      key = `${school}|${bandLabel}`;
      label = `${school} · ${bandLabel}`;
      sort = [school, rowBand ? BAND_WEIGHT[rowBand] : 3];
    } else {
      // Chinese and Math: the class already *is* the grade, so read it from the key rather
      // than from the child's record — they agree, and the key is what the row belongs to.
      label = attendanceClassLabel(row.virtualClassKey, row.programName);
      key = label;
      const normalized = normalizeGrade(row.studentGrade);
      sort = ['', normalized ? gradeRank(normalized) : 9999];
    }

    const existing = sections.get(key);
    if (existing) existing.rows.push(row);
    else sections.set(key, { key, label, rows: [row], sort });
  }

  return [...sections.values()]
    .sort((a, b) => a.sort[0].localeCompare(b.sort[0]) || a.sort[1] - b.sort[1] || a.label.localeCompare(b.label))
    .map(({ key, label, rows: sectionRows }) => ({ key, label, rows: sectionRows }));
}

/** Grade options for the Chinese/Math filter: the seven grades those classes run. */
export function virtualGradeOptions(
  band: 'chinese' | 'math',
  grades: readonly string[],
): { value: string; label: string }[] {
  return grades.map(grade => ({
    value: `${band}:${grade.toLowerCase()}`,
    label: formatGradeLabel(grade),
  }));
}
